import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Trash2, Mail, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { PageTitle } from "./AdminKit";
import { api, formatErr, waLink } from "../../lib/api";

const COLORS = { New: "bg-sky-100 text-sky-800", Contacted: "bg-amber-100 text-amber-800", Closed: "bg-emerald-100 text-emerald-800" };
export const StatusPill = ({ status }) => <span className={`px-2.5 py-1 text-xs font-semibold ${COLORS[status] || "bg-slate-100"}`}>{status}</span>;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

const Detail = ({ r, onClose, onSave }) => {
  const [note, setNote] = useState(r?.notes || "");
  if (!r) return null;
  const rows = [["Company", r.company], ["Product", r.product], ["Quantity", r.quantity], ["City", r.city], ["Message", r.message], ["Email alert", r.email_status], ["WhatsApp alert", r.whatsapp_status]];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-inquiry-detail" className="max-w-lg rounded-none">
        <DialogTitle className="font-display text-3xl font-extrabold uppercase">{r.name}</DialogTitle>
        <p className="text-xs text-slate-500">#{r.id.slice(0, 8).toUpperCase()} · {fmt(r.created_at)}</p>
        <div className="flex flex-wrap gap-2">
          <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1.5 bg-slate-900 px-3 py-2 text-xs font-semibold text-white"><Phone className="h-3.5 w-3.5" />{r.phone}</a>
          <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 bg-slate-100 px-3 py-2 text-xs font-semibold"><Mail className="h-3.5 w-3.5" />{r.email}</a>
          <a href={waLink(r.phone, `Hello ${r.name}, this is Autocrat Rubber Industries regarding your RFQ for ${r.product}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] px-3 py-2 text-xs font-semibold text-white"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
        </div>
        <dl className="divide-y divide-slate-100 text-sm">
          {rows.map(([k, v]) => <div key={k} className="grid grid-cols-3 gap-3 py-2"><dt className="text-slate-500">{k}</dt><dd className="col-span-2 whitespace-pre-wrap text-slate-900">{v || "-"}</dd></div>)}
        </dl>
        <label className="block text-sm font-medium">Follow-up notes
          <textarea data-testid="admin-inquiry-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5 w-full border border-slate-300 px-3 py-2 text-sm font-normal outline-none focus:border-[#0a6cff]" />
        </label>
        <button data-testid="admin-inquiry-save-note" onClick={() => onSave(r, r.status, note)} className="btn-electric">Save notes</button>
      </DialogContent>
    </Dialog>
  );
};

export const Inquiries = () => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const { data } = useQuery({ queryKey: ["admin-inquiries", filter], queryFn: () => api.get("/admin/inquiries", { params: filter ? { status: filter } : {} }).then((r) => r.data) });
  const rows = Array.isArray(data) ? data : [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-inquiries"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const update = async (r, status, note = r.notes || "") => {
    try { await api.patch(`/admin/inquiries/${r.id}`, { status, note }); toast.success(`Marked as ${status}`); refresh(); setOpen(null); } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete inquiry from ${r.name}?`)) return;
    await api.delete(`/admin/inquiries/${r.id}`); toast.success("Deleted"); refresh();
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/inquiries-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `autocrat-inquiries-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageTitle title="Inquiries" desc="Every RFQ from the website. Click a row to see details and add notes.">
        <div className="flex">
          {["", "New", "Contacted", "Closed"].map((s) => (
            <button key={s || "all"} data-testid={`admin-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`px-3 py-2 text-xs font-semibold ${filter === s ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>{s || "All"}</button>
          ))}
        </div>
        <button data-testid="admin-export-csv-button" onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#0a6cff] px-4 py-2 text-xs font-semibold text-white"><Download className="h-4 w-4" />Export CSV</button>
      </PageTitle>
      <div className="overflow-x-auto bg-white" data-testid="admin-inquiries-table">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-4">Date</th><th className="p-4">Contact</th><th className="p-4">Product / Qty</th><th className="p-4">Phone</th><th className="p-4">Status</th><th className="p-4" /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`admin-inquiry-row-${r.id.slice(0, 8)}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(r)}>
                <td className="p-4 text-slate-500">{fmt(r.created_at)}</td>
                <td className="p-4"><p className="font-semibold text-slate-900">{r.name}</p><p className="text-slate-500">{r.company}</p></td>
                <td className="p-4"><p>{r.product}</p><p className="text-slate-500">{r.quantity}</p></td>
                <td className="p-4">{r.phone}</td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <Select value={r.status} onValueChange={(v) => update(r, v)}>
                    <SelectTrigger data-testid="admin-status-dropdown" className="h-9 w-36 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{["New", "Contacted", "Closed"].map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-option-${s}`}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => remove(r)} data-testid="admin-inquiry-delete" className="p-2 text-slate-400 hover:text-red-500" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="p-8 text-sm text-slate-500">No inquiries {filter && `with status "${filter}"`} yet.</p>}
      </div>
      {open && <Detail r={open} onClose={() => setOpen(null)} onSave={update} />}
    </div>
  );
};
